import type { BadgeTone } from '../components/ui';
import type { Locale, TranslationKey } from './i18n';

/**
 * AI daily points (CR-007, 2026-11-04) — what the admin's pages read. Everything here is the
 * administrators' (`ai_points.view_all` / `ai_points.review`): the reasons, evidence and decisions never
 * reach an employee, who sees only the points credited in their wallet and one thank-you.
 */

export const DIMENSIONS = [
  'REGULAR_EXTRA_HOURS',
  'FREE_SHIFT',
  'TASKS_ABOVE_BASELINE',
  'EXTRA_OPERATIONAL',
  'CALLS_COLLABORATION',
] as const;
export type DimensionKey = (typeof DIMENSIONS)[number];

export type EvaluationStatus =
  | 'QUEUED'
  | 'EVALUATING'
  | 'PENDING_ADMIN_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'DEFERRED'
  | 'FAILED_AI'
  | 'BLOCKED_CONFIGURATION'
  | 'NOT_ELIGIBLE';

export const STATUSES: readonly EvaluationStatus[] = [
  'PENDING_ADMIN_APPROVAL',
  'APPROVED',
  'REJECTED',
  'DEFERRED',
  'FAILED_AI',
  'BLOCKED_CONFIGURATION',
  'NOT_ELIGIBLE',
  'QUEUED',
  'EVALUATING',
];

export type RunKind = 'SCHEDULED' | 'INITIAL_YESTERDAY_TRIAL' | 'MANUAL';
export type RunStatus = 'EVALUATING' | 'READY' | 'BLOCKED_CONFIGURATION';

export const STATUS_TONE: Record<EvaluationStatus, BadgeTone> = {
  QUEUED: 'neutral',
  EVALUATING: 'info',
  PENDING_ADMIN_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'neutral',
  DEFERRED: 'info',
  FAILED_AI: 'error',
  BLOCKED_CONFIGURATION: 'error',
  NOT_ELIGIBLE: 'neutral',
};

export const RUN_TONE: Record<RunStatus, BadgeTone> = { EVALUATING: 'info', READY: 'success', BLOCKED_CONFIGURATION: 'error' };

export interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

export interface RunSummary {
  id: string;
  workDate: string;
  runKind: RunKind;
  servedAsInitialTrial: boolean;
  status: RunStatus;
  scheduledFor: string | null;
  startedAt: string;
  preparedAt: string | null;
  finishedAt: string | null;
  dataCutoffAt: string;
  configVersion: number;
  timezone: string;
  lastError: string | null;
  counts: Partial<Record<EvaluationStatus, number>>;
  zeroApprovals: number;
  creditedPoints: number;
  attempts: number;
  modelCalls: number;
  inputTokens: number;
  outputTokens: number;
}

export interface Overview {
  server: {
    ready: boolean;
    enabled: boolean;
    configVersion: number;
    model: string | null;
    scheduleTime: string;
    timezone: string;
    nextRunAt: string | null;
  };
  initialTrial: { claimedAt: string; workDate: string; runId: string | null } | null;
  canReview: boolean;
  runs: RunSummary[];
}

export interface Figures {
  dayKind: string | null;
  judged: string | null;
  standardMinutes: number | null;
  workedMinutes: number | null;
  surplusMinutes: number;
  freeMinutes: number;
  freeSessions: number;
  tasksFinished: number;
  tasksVerified: number;
  tasksAwaitingReview: number;
  tickets: number;
  qTasks: number;
  meaningfulCalls: number;
  callMinutes: number;
  reviews: number;
  comments: number;
  flags: string[];
}

export interface EvaluationRow {
  id: string;
  workDate: string;
  status: EvaluationStatus;
  statusReason: string | null;
  revision: number;
  score: number | null;
  creditedPoints: number | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  uncertainty: 'LOW' | 'MEDIUM' | 'HIGH' | null;
  attempts: number;
  lastTrigger: string | null;
  generatedAt: string | null;
  dataCutoffAt: string | null;
  reviewedAt: string | null;
  reviewedBy: Person | null;
  walletTransactionId: string | null;
  run: { id: string; runKind: RunKind; servedAsInitialTrial: boolean; status: RunStatus };
  employee: Person & {
    employeeCode: string | null;
    isActive: boolean;
    departments: Array<{ id: string; name: string; nameAr: string | null }>;
  };
  figures: Figures | null;
  self: boolean;
  needsConfirmation: boolean;
  selfNotice: 'OTHER_REVIEWER' | 'NO_OTHER_REVIEWER' | null;
  can: { approve: boolean; reject: boolean; retry: boolean };
}

export interface EvaluationList {
  items: EvaluationRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  counts: Partial<Record<EvaluationStatus, number>>;
}

export interface Dimension {
  key: DimensionKey;
  points: number;
  max: number;
  cap: number;
  reasonEn: string;
  reasonAr: string;
  evidenceIds: string[];
}

export type EvidenceKind = 'ATTENDANCE' | 'FREE_SHIFT' | 'TASK' | 'TICKET' | 'Q_TASK' | 'CALL' | 'REVIEW' | 'COMMENTS' | 'BASELINE';

export interface EvidenceItem {
  id: string;
  kind: EvidenceKind;
  crossTeam?: boolean;
  sourceType: 'ATTENDANCE_SESSION' | 'TASK' | 'TICKET' | 'Q_TASK' | 'CALL' | null;
  sourceId: string | null;
  label: string | null;
  code: string | null;
  at: string | null;
  facts: Record<string, unknown>;
  /** Whether the record is still there: a deleted one keeps its snapshot. Null for figures with no record. */
  available: boolean | null;
}

export interface Attempt {
  id: string;
  revision: number;
  attemptNo: number;
  trigger: string;
  outcome: 'PROPOSED' | 'INVALID_OUTPUT' | 'PROVIDER_ERROR' | 'BLOCKED_CONFIGURATION' | 'DEFERRED' | 'NOT_ELIGIBLE' | 'REQUEUED';
  errorCode: string | null;
  validationErrors: string[] | null;
  output: unknown;
  aiModel: string | null;
  inputDigest: string | null;
  dataCutoffAt: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  reasoningTokens: number | null;
  durationMs: number | null;
  reason: string | null;
  createdAt: string;
  requestedBy: Person | null;
}

export interface Cap {
  key: DimensionKey;
  max: number;
  cap: number;
  why: string;
}

export interface EvaluationDetail extends Omit<EvaluationRow, 'run'> {
  run: {
    id: string;
    runKind: RunKind;
    servedAsInitialTrial: boolean;
    status: RunStatus;
    timezone: string;
    dayStartsAt: string;
    scheduledFor: string | null;
    startedAt: string;
    finishedAt: string | null;
    dataCutoffAt: string;
    configVersion: number;
  };
  dimensions: Dimension[] | null;
  summaryEn: string | null;
  summaryAr: string | null;
  excluded: Array<{ evidenceId: string; whyEn: string; whyAr: string }> | null;
  metrics: Record<string, any> | null;
  flags: string[] | null;
  evidence: EvidenceItem[];
  modelInput: Record<string, unknown> | null;
  rubricVersion: number | null;
  rubricSnapshot: { configVersion: number; rubric: Record<DimensionKey, number>; thresholds: Record<string, unknown>; caps: Cap[]; promptVersion: string } | null;
  aiModel: string | null;
  promptVersion: string | null;
  inputDigest: string | null;
  rejectNote: string | null;
  createdAt: string;
  attemptHistory: Attempt[];
}

/** What Approve all would approve in a run — each with the revision shown, so exactly that is approved. */
export interface ApproveAllPreview {
  runId: string;
  workDate: string;
  items: Array<{ id: string; revision: number; score: number; needsConfirmation: boolean; employee: Person }>;
  count: number;
  points: number;
  withPoints: number;
  lowConfidence: number;
  skipped: { SELF: number; ADMINISTRATOR: number; INACTIVE: number };
}

export interface ApproveAllResult {
  approved: number;
  replayed: number;
  creditedPoints: number;
  failed: Array<{ id: string; code: string | null; reason: string | null }>;
}

export interface AiPointsConfig {
  enabled: boolean;
  rubric: Record<DimensionKey, number>;
  regularMinutesPerPoint: number;
  freeMinutesPerPoint: number;
  baselineWindowDays: number;
  minBaselineDays: number;
  minCallMinutes: number;
  includeCallSummaries: boolean;
  model: string;
}

export interface SettingsView {
  version: number;
  config: AiPointsConfig;
  defaults: AiPointsConfig;
  versions: Array<{ version: number; createdAt: string; createdBy: Person | null }>;
  server: { ready: boolean; models: string[]; model: string | null; scheduleTime: string; timezone: string; jobsEnabled: boolean };
}

/** A work date (YYYY-MM-DD) as the page shows it — read as a calendar day, the same wherever the browser is. */
export function formatWorkDate(date: string, locale: Locale): string {
  const tag = locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB';
  return new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
}

/** The record behind a piece of evidence, on its own page — which keeps its own permissions. */
export function evidenceLink(item: Pick<EvidenceItem, 'sourceType' | 'sourceId'>): string | null {
  if (!item.sourceId) return null;
  switch (item.sourceType) {
    case 'TASK':
      return `/tasks/${item.sourceId}`;
    case 'TICKET':
      return `/tickets/${item.sourceId}`;
    case 'Q_TASK':
      return `/q-tasks/${item.sourceId}`;
    case 'CALL':
      return `/calls/${item.sourceId}`;
    case 'ATTENDANCE_SESSION':
      return `/attendance/sessions/${item.sourceId}`;
    default:
      return null;
  }
}

/** The reason, summary or note in the page's language — the other when that one is empty. */
export const inLocale = (locale: Locale, en: string | null | undefined, ar: string | null | undefined) =>
  (locale === 'ar' ? ar || en : en || ar) ?? '';

/** A dictionary key built from a code the API sends, with the code itself when the page has no words for it. */
export function codeText(t: (k: TranslationKey) => string, prefix: string, code: string | null | undefined): string {
  if (!code) return '';
  const key = `${prefix}.${code}` as TranslationKey;
  const text = t(key);
  return text === key ? code : text;
}
